import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Code, ConnectError } from '@connectrpc/connect';
import { useAuditCodebookSource } from '../AuditDataSource';
import { AUDIT_CODEBOOK } from './codebook';
import type { CodebookEntry, CodebookHit, CodeKind } from '../types';

// Code lookup against the SERVER codebook.
//
// # What this replaces
//
// `acode()` read a hand-maintained map of ~130 entries compiled into the bundle,
// and the workbench rendered "Uncatalogued code" for anything outside it. The
// server has had a real codebook the whole time — SearchCodebook, backed by the
// licensed CPT/HCPCS files and now ICD-10-CM — and the UI never once asked it.
// So the fix for "codes show as unknown" is only partly the missing ICD loader;
// the other half is that the question was being put to the wrong place.
//
// # Why the built-in map survives
//
// As an OFFLINE fallback, not as the answer. The backendless preview and the
// tests have no server, and a code chip that renders nothing there makes those
// surfaces useless for judging layout. It is consulted only after the server
// has been asked and could not answer.
//
// # The distinction this is careful to keep
//
// The server answers a miss two different ways on purpose: NotFound means the
// loaded codeset does not contain the code, FailedPrecondition means no codeset
// is loaded at all. On a screen where someone is deciding whether a code is
// correct, collapsing those into one message tells a reviewer that a valid code
// does not exist. They stay separate all the way to the chip.

/** How a single code resolved. */
export type CodeLookup =
  | { status: 'loading' }
  /**
   * Resolved, from the server or from the offline fallback.
   *
   * `related` is what the server sent ALONGSIDE the exact match — for a
   * surgical procedure code, the anesthesia codes the crosswalk says are
   * billable with it. The lookup used to discard them, so a reviewer judging
   * whether an ASA code belongs with a CPT had the answer arriving on the wire
   * and no way to see it.
   */
  | {
      status: 'found';
      entry: CodebookEntry;
      offline: boolean;
      related: CodebookHit[];
    }
  /** The loaded codeset genuinely does not contain this code. */
  | { status: 'unknown' }
  /** No codeset is loaded, so we cannot say whether the code exists. */
  | { status: 'no-codeset' }
  | { status: 'error' };

/** The offline map, shaped like a lookup result. */
function builtin(code: string): CodebookEntry | undefined {
  return AUDIT_CODEBOOK[code.toUpperCase()] ?? AUDIT_CODEBOOK[code];
}

function hitToEntry(hit: CodebookHit): CodebookEntry {
  return { kind: hit.kind, title: hit.title, desc: hit.desc };
}

/**
 * Classifies a thrown lookup error.
 *
 * Exported for the search hook, which needs the same three-way answer.
 */
export function classifyCodebookError(error: unknown): 'unknown' | 'no-codeset' | 'error' {
  if (error instanceof ConnectError) {
    if (error.code === Code.NotFound) return 'unknown';
    if (error.code === Code.FailedPrecondition) return 'no-codeset';
  }
  return 'error';
}

/**
 * Resolves one code's meaning.
 *
 * Cached indefinitely per code: the codeset is reference data that changes on a
 * quarterly release, not per session, and a compare screen asks for the same
 * dozen codes on every case a reviewer opens.
 */
export function useCodeEntry(code: string): CodeLookup {
  const source = useAuditCodebookSource();
  const trimmed = code.trim();

  const q = useQuery({
    queryKey: ['audit', 'codebook', 'code', trimmed],
    enabled: trimmed.length > 0,
    // Reference data. Refetching it costs a round trip to learn what we already
    // know, on the screen this change exists to make responsive.
    staleTime: Infinity,
    gcTime: Infinity,
    // A miss is an authoritative answer, not a transient failure. Retrying it
    // three times delays the "unknown code" chip for no gain.
    retry: false,
    queryFn: async () => {
      const hits = await source.searchCodebook(trimmed);
      const exact = hits.find((h) => h.code.toUpperCase() === trimmed.toUpperCase());
      if (!exact) return null;
      // Everything else the server returned for an EXACT hit is the crosswalk
      // it chose to send. Only kept when the exact match was found: without
      // one, `hits` is a near-miss list, and presenting those as "related
      // codes" would tell a reviewer the codeset links codes it does not.
      return { exact, related: hits.filter((h) => h.code !== exact.code) };
    },
  });

  return useMemo((): CodeLookup => {
    if (!trimmed) return { status: 'unknown' };
    if (q.isPending) return { status: 'loading' };

    if (q.isError) {
      const kind = classifyCodebookError(q.error);
      // Fall back to the built-in map ONLY when the server has no codeset. If
      // the server has one and says the code is absent, that is the answer —
      // overriding it with a stale bundled entry would tell a reviewer a
      // retired code is current, which is the specific harm the server-side
      // built-ins were removed to avoid.
      if (kind === 'no-codeset') {
        const e = builtin(trimmed);
        // The offline map has no crosswalk, so `related` is empty rather than
        // stale — the modal says nothing about billable pairings instead of
        // guessing at them.
        if (e) return { status: 'found', entry: e, offline: true, related: [] };
        return { status: 'no-codeset' };
      }
      return { status: kind };
    }

    if (q.data)
      return {
        status: 'found',
        entry: hitToEntry(q.data.exact),
        offline: false,
        related: q.data.related,
      };
    // A successful search that matched nothing exactly. The server reports a
    // true miss as NotFound, so this is a near-miss list — treat it as unknown.
    return { status: 'unknown' };
  }, [trimmed, q.isPending, q.isError, q.error, q.data]);
}

/** A codebook search, for the reviewer's code picker. */
export interface CodebookSearch {
  status: 'loading' | 'ready' | 'no-codeset' | 'error';
  hits: CodebookHit[];
}

/**
 * Searches the codebook.
 *
 * Server-side, so the reviewer searches the whole loaded codeset rather than the
 * bundle's ~130 entries — the picker previously could not offer a code the
 * agent had just assigned unless someone had hand-added it here.
 */
export function useCodebookSearch(query: string, kind?: CodeKind | ''): CodebookSearch {
  const source = useAuditCodebookSource();
  const trimmed = query.trim();

  const q = useQuery({
    queryKey: ['audit', 'codebook', 'search', trimmed, kind ?? ''],
    enabled: trimmed.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: () => source.searchCodebook(trimmed),
  });

  return useMemo((): CodebookSearch => {
    if (!trimmed) return { status: 'ready', hits: [] };
    if (q.isPending) return { status: 'loading', hits: [] };
    if (q.isError) {
      const cls = classifyCodebookError(q.error);
      // A search that finds nothing is an empty result, not an error — only a
      // missing codeset is worth telling the reviewer about.
      if (cls === 'unknown') return { status: 'ready', hits: [] };
      return { status: cls === 'no-codeset' ? 'no-codeset' : 'error', hits: [] };
    }
    const hits = kind ? (q.data ?? []).filter((h) => h.kind === kind) : (q.data ?? []);
    return { status: 'ready', hits };
  }, [trimmed, kind, q.isPending, q.isError, q.error, q.data]);
}
